import React, { useCallback, useEffect, useState } from 'react';
import {
  createAttributeDefinition,
  createAttributeOptionDefinition,
  deleteAttributeDefinition,
  deleteAttributeOptionDefinition,
  getStoredAdminToken,
  listAttributeDefinitions,
  updateAttributeDefinition,
  updateAttributeOptionDefinition,
  uploadAdminImage,
} from '../services/adminAuthApi';
import { resolveMediaUrl } from '../services/productsApi';

const ATTRIBUTE_TYPES = [
  { value: 'color', label: 'Color (círculo)' },
  { value: 'image', label: 'Acabado (imagen + nombre)' },
  { value: 'text', label: 'Texto (+ imagen opcional)' },
];

const attributeSupportsOptionImage = (attributeType) =>
  attributeType === 'image' || attributeType === 'text';

const GRADE_OPTIONS = [
  { value: '', label: 'Sin grado' },
  { value: '0', label: 'Grado 0' },
  { value: '1', label: 'Grado 1' },
  { value: '2', label: 'Grado 2' },
  { value: '3', label: 'Grado 3' },
  { value: 'A', label: 'Grado A' },
];

const emptyAttributeForm = {
  label: '',
  attributeType: 'color',
  grade: '1',
};

const emptyOptionForm = {
  label: '',
  hexColor: '#cccccc',
  imagePath: '',
};

const AdminAttributeCatalog = ({ onCatalogChange }) => {
  const [definitions, setDefinitions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isExpanded, setIsExpanded] = useState(false);
  const [expandedAttributeId, setExpandedAttributeId] = useState(null);
  const [attributeForm, setAttributeForm] = useState(emptyAttributeForm);
  const [optionForms, setOptionForms] = useState({});
  const [isSavingAttribute, setIsSavingAttribute] = useState(false);
  const [savingOptionFor, setSavingOptionFor] = useState(null);
  const [uploadingOptionFor, setUploadingOptionFor] = useState(null);
  const [statusMessage, setStatusMessage] = useState('');
  const [statusType, setStatusType] = useState('');

  const loadDefinitions = useCallback(async () => {
    const token = getStoredAdminToken();
    if (!token) return;
    setLoading(true);
    try {
      const list = await listAttributeDefinitions(token);
      setDefinitions(Array.isArray(list) ? list : []);
      if (onCatalogChange) onCatalogChange(Array.isArray(list) ? list : []);
    } catch {
      setDefinitions([]);
      if (onCatalogChange) onCatalogChange([]);
    } finally {
      setLoading(false);
    }
  }, [onCatalogChange]);

  useEffect(() => {
    loadDefinitions();
  }, [loadDefinitions]);

  const getOptionForm = (attributeId) =>
    optionForms[attributeId] || emptyOptionForm;

  const updateOptionForm = (attributeId, patch) => {
    setOptionForms((prev) => ({
      ...prev,
      [attributeId]: {
        ...getOptionForm(attributeId),
        ...patch,
      },
    }));
  };

  const handleCreateAttribute = async (event) => {
    event.preventDefault();
    const token = getStoredAdminToken();
    if (!token || !attributeForm.label.trim()) return;

    setIsSavingAttribute(true);
    setStatusMessage('');
    try {
      await createAttributeDefinition(token, {
        label: attributeForm.label.trim(),
        attributeType: attributeForm.attributeType,
        grade:
          attributeForm.attributeType === 'image'
            ? attributeForm.grade || null
            : null,
      });
      setAttributeForm(emptyAttributeForm);
      setStatusType('success');
      setStatusMessage('Atributo creado en el catálogo.');
      await loadDefinitions();
    } catch (err) {
      setStatusType('error');
      setStatusMessage(err.message || 'No se pudo crear el atributo.');
    } finally {
      setIsSavingAttribute(false);
    }
  };

  const handleOptionImageUpload = async (attributeId, event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    const token = getStoredAdminToken();
    if (!token) return;

    setUploadingOptionFor(attributeId);
    setStatusMessage('');
    try {
      const result = await uploadAdminImage(token, file);
      updateOptionForm(attributeId, { imagePath: result.path || '' });
      setStatusType('success');
      setStatusMessage('Imagen subida.');
    } catch (err) {
      setStatusType('error');
      setStatusMessage(err.message || 'No se pudo subir la imagen.');
    } finally {
      setUploadingOptionFor(null);
    }
  };

  const handleExistingOptionImageUpload = async (option, event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || !option?.id) return;

    const token = getStoredAdminToken();
    if (!token) return;

    const uploadKey = `existing-${option.id}`;
    setUploadingOptionFor(uploadKey);
    setStatusMessage('');
    try {
      const result = await uploadAdminImage(token, file);
      await updateAttributeOptionDefinition(token, option.id, {
        imagePath: result.path || '',
      });
      setStatusType('success');
      setStatusMessage(`Imagen agregada a "${option.label}".`);
      await loadDefinitions();
    } catch (err) {
      setStatusType('error');
      setStatusMessage(err.message || 'No se pudo subir la imagen.');
    } finally {
      setUploadingOptionFor(null);
    }
  };

  const handleCreateOption = async (event, attribute) => {
    event.preventDefault();
    const token = getStoredAdminToken();
    const form = getOptionForm(attribute.id);
    if (!token || !form.label.trim()) return;

    const supportsImage = attributeSupportsOptionImage(attribute.attribute_type);

    if (attribute.attribute_type === 'image' && !form.imagePath) {
      setStatusType('error');
      setStatusMessage('Para acabados, sube una imagen junto con el nombre.');
      return;
    }

    setSavingOptionFor(attribute.id);
    setStatusMessage('');
    try {
      await createAttributeOptionDefinition(token, attribute.id, {
        label: form.label.trim(),
        hexColor:
          attribute.attribute_type === 'color' ? form.hexColor : null,
        imagePath: supportsImage ? form.imagePath || null : null,
      });
      updateOptionForm(attribute.id, emptyOptionForm);
      setStatusType('success');
      setStatusMessage(`Opción agregada a "${attribute.label}".`);
      await loadDefinitions();
    } catch (err) {
      setStatusType('error');
      setStatusMessage(err.message || 'No se pudo crear la opción.');
    } finally {
      setSavingOptionFor(null);
    }
  };

  const handleDeleteAttribute = async (attribute) => {
    if (
      !window.confirm(
        `¿Eliminar el atributo "${attribute.label}" del catálogo?`
      )
    ) {
      return;
    }
    const token = getStoredAdminToken();
    if (!token) return;
    try {
      await deleteAttributeDefinition(token, attribute.id);
      setStatusType('success');
      setStatusMessage('Atributo eliminado del catálogo.');
      await loadDefinitions();
    } catch (err) {
      setStatusType('error');
      setStatusMessage(err.message || 'No se pudo eliminar el atributo.');
    }
  };

  const handleGradeChange = async (attribute, nextGrade) => {
    const token = getStoredAdminToken();
    if (!token) return;
    setStatusMessage('');
    try {
      await updateAttributeDefinition(token, attribute.id, {
        grade: nextGrade === '' ? null : String(nextGrade).toUpperCase(),
        attributeType: attribute.attribute_type,
      });
      setStatusType('success');
      setStatusMessage(
        nextGrade
          ? `Grado ${String(nextGrade).toUpperCase()} asignado a "${attribute.label}".`
          : `Se quitó el grado de "${attribute.label}".`
      );
      await loadDefinitions();
    } catch (err) {
      setStatusType('error');
      setStatusMessage(err.message || 'No se pudo actualizar el grado.');
    }
  };

  const handleDeleteOption = async (attribute, option) => {
    if (
      !window.confirm(
        `¿Eliminar la opción "${option.label}" de "${attribute.label}"?`
      )
    ) {
      return;
    }
    const token = getStoredAdminToken();
    if (!token) return;
    try {
      await deleteAttributeOptionDefinition(token, option.id);
      setStatusType('success');
      setStatusMessage('Opción eliminada.');
      await loadDefinitions();
    } catch (err) {
      setStatusType('error');
      setStatusMessage(err.message || 'No se pudo eliminar la opción.');
    }
  };

  const typeLabel = (type) =>
    ATTRIBUTE_TYPES.find((item) => item.value === type)?.label || type;

  return (
    <div className="admin-attribute-catalog-section">
      <button
        type="button"
        className="admin-product-list-toggle"
        onClick={() => setIsExpanded((value) => !value)}
        aria-expanded={isExpanded}
        aria-controls="admin-attribute-catalog-panel"
        id="admin-attribute-catalog-heading"
      >
        <span className="admin-product-list-toggle-text">
          <span className="admin-panel-section-title admin-product-list-title-inline">
            Catálogo de atributos
          </span>
          {!loading && (
            <span className="admin-product-list-count">
              {definitions.length}{' '}
              {definitions.length === 1 ? 'atributo' : 'atributos'}
            </span>
          )}
        </span>
        <span
          className={`admin-product-list-chevron ${isExpanded ? 'expanded' : ''}`}
          aria-hidden="true"
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </span>
      </button>

      {isExpanded && (
        <div
          id="admin-attribute-catalog-panel"
          role="region"
          aria-labelledby="admin-attribute-catalog-heading"
        >
          <p className="admin-panel-hint">
            Crea atributos reutilizables. Usa <strong>Acabado</strong> para telas
            o texturas (nombre + imagen de muestra) y asígnales un{' '}
            <strong>grado</strong> (0, 1, 2, 3 o A).
          </p>

          <form
            className="admin-attribute-create-form"
            onSubmit={handleCreateAttribute}
          >
            <div className="admin-panel-grid">
              <div className="form-row">
                <label htmlFor="admin-attribute-label">Nuevo atributo</label>
                <input
                  id="admin-attribute-label"
                  type="text"
                  value={attributeForm.label}
                  onChange={(e) =>
                    setAttributeForm((prev) => ({
                      ...prev,
                      label: e.target.value,
                    }))
                  }
                  placeholder="Ej. Cuerpo, Addison, Tela"
                  disabled={isSavingAttribute}
                />
              </div>
              <div className="form-row">
                <label htmlFor="admin-attribute-type">Tipo</label>
                <select
                  id="admin-attribute-type"
                  value={attributeForm.attributeType}
                  onChange={(e) =>
                    setAttributeForm((prev) => ({
                      ...prev,
                      attributeType: e.target.value,
                      grade:
                        e.target.value === 'image'
                          ? prev.grade || '1'
                          : '',
                    }))
                  }
                  disabled={isSavingAttribute}
                >
                  {ATTRIBUTE_TYPES.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>
              {attributeForm.attributeType === 'image' && (
                <div className="form-row">
                  <label htmlFor="admin-attribute-grade">Grado</label>
                  <select
                    id="admin-attribute-grade"
                    value={attributeForm.grade}
                    onChange={(e) =>
                      setAttributeForm((prev) => ({
                        ...prev,
                        grade: e.target.value,
                      }))
                    }
                    disabled={isSavingAttribute}
                  >
                    {GRADE_OPTIONS.filter((option) => option.value !== '').map(
                      (option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      )
                    )}
                  </select>
                </div>
              )}
            </div>
            <button
              type="submit"
              className="admin-auth-btn secondary admin-attribute-create-btn"
              disabled={isSavingAttribute || !attributeForm.label.trim()}
            >
              {isSavingAttribute ? 'Creando...' : 'Crear atributo'}
            </button>
          </form>

          {loading ? (
            <p className="admin-panel-hint">Cargando catálogo...</p>
          ) : definitions.length === 0 ? (
            <p className="admin-panel-hint">
              Aún no hay atributos. Crea el primero arriba.
            </p>
          ) : (
            <div className="admin-attribute-list">
              {definitions.map((attribute) => {
                const options = Array.isArray(attribute.options)
                  ? attribute.options
                  : [];
                const isAttributeExpanded = expandedAttributeId === attribute.id;
                const optionForm = getOptionForm(attribute.id);
                const isImageType = attribute.attribute_type === 'image';
                const supportsOptionImage = attributeSupportsOptionImage(
                  attribute.attribute_type
                );

                return (
                  <div className="admin-attribute-card" key={attribute.id}>
                    <button
                      type="button"
                      className="admin-attribute-card-toggle"
                      onClick={() =>
                        setExpandedAttributeId((current) =>
                          current === attribute.id ? null : attribute.id
                        )
                      }
                      aria-expanded={isAttributeExpanded}
                    >
                      <span>
                        <strong>{attribute.label}</strong>
                        <span className="admin-attribute-meta">
                          {typeLabel(attribute.attribute_type)}
                          {attribute.attribute_type === 'image' &&
                          attribute.grade
                            ? ` · Grado ${attribute.grade}`
                            : ''}
                          {' · '}
                          {options.length}{' '}
                          {options.length === 1 ? 'valor' : 'valores'}
                        </span>
                      </span>
                      <span
                        className={`admin-product-list-chevron ${
                          isAttributeExpanded ? 'expanded' : ''
                        }`}
                        aria-hidden="true"
                      >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                          <polyline points="6 9 12 15 18 9" />
                        </svg>
                      </span>
                    </button>

                    {isAttributeExpanded && (
                      <div className="admin-attribute-card-body">
                        {isImageType && (
                          <div className="form-row admin-attribute-grade-row">
                            <label htmlFor={`attribute-grade-${attribute.id}`}>
                              Grado de esta línea
                            </label>
                            <select
                              id={`attribute-grade-${attribute.id}`}
                              value={
                                attribute.grade == null
                                  ? ''
                                  : String(attribute.grade)
                              }
                              onChange={(e) =>
                                handleGradeChange(attribute, e.target.value)
                              }
                            >
                              {GRADE_OPTIONS.map((option) => (
                                <option key={option.value || 'none'} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </select>
                          </div>
                        )}

                        <div
                          className={`admin-attribute-options-list ${
                            supportsOptionImage ? 'is-image-grid' : ''
                          }`}
                        >
                          {options.length === 0 ? (
                            <p className="admin-panel-hint">
                              Sin valores todavía.
                            </p>
                          ) : (
                            options.map((option) => {
                              const existingUploadKey = `existing-${option.id}`;
                              const isUploadingExisting =
                                uploadingOptionFor === existingUploadKey;
                              return (
                              <div
                                className={`admin-attribute-option-row ${
                                  supportsOptionImage ? 'is-finish' : ''
                                }`}
                                key={option.id}
                              >
                                {attribute.attribute_type === 'color' &&
                                  option.hex_color && (
                                  <span
                                    className="admin-attribute-swatch"
                                    style={{ backgroundColor: option.hex_color }}
                                    title={option.hex_color}
                                  />
                                )}
                                {supportsOptionImage && option.image_path && (
                                  <img
                                    src={resolveMediaUrl(option.image_path)}
                                    alt={option.label}
                                    className="admin-attribute-finish-thumb"
                                  />
                                )}
                                <span>{option.label}</span>
                                {supportsOptionImage && (
                                  <div className="admin-attribute-option-image-actions">
                                    <label
                                      className={`admin-file-btn admin-file-btn-small ${
                                        isUploadingExisting ? 'disabled' : ''
                                      }`}
                                      htmlFor={`existing-option-image-${option.id}`}
                                    >
                                      {isUploadingExisting
                                        ? 'Subiendo...'
                                        : option.image_path
                                          ? 'Cambiar imagen'
                                          : 'Agregar imagen'}
                                    </label>
                                    <input
                                      id={`existing-option-image-${option.id}`}
                                      type="file"
                                      accept="image/*"
                                      onChange={(e) =>
                                        handleExistingOptionImageUpload(
                                          option,
                                          e
                                        )
                                      }
                                      disabled={isUploadingExisting}
                                    />
                                  </div>
                                )}
                                <button
                                  type="button"
                                  className="admin-table-btn danger"
                                  onClick={() =>
                                    handleDeleteOption(attribute, option)
                                  }
                                >
                                  Eliminar
                                </button>
                              </div>
                              );
                            })
                          )}
                        </div>

                        <form
                          className="admin-attribute-option-form"
                          onSubmit={(event) =>
                            handleCreateOption(event, attribute)
                          }
                        >
                          <div className="admin-panel-grid">
                            <div className="form-row">
                              <label htmlFor={`option-label-${attribute.id}`}>
                                Nombre
                              </label>
                              <input
                                id={`option-label-${attribute.id}`}
                                type="text"
                                value={optionForm.label}
                                onChange={(e) =>
                                  updateOptionForm(attribute.id, {
                                    label: e.target.value,
                                  })
                                }
                                placeholder={
                                  isImageType
                                    ? 'Ej. Verde Hoja'
                                    : 'Ej. Verde militar'
                                }
                                disabled={savingOptionFor === attribute.id}
                              />
                            </div>
                            {attribute.attribute_type === 'color' && (
                              <div className="form-row">
                                <label htmlFor={`option-color-${attribute.id}`}>
                                  Color
                                </label>
                                <div className="admin-color-input-row">
                                  <input
                                    id={`option-color-${attribute.id}`}
                                    type="color"
                                    value={optionForm.hexColor}
                                    onChange={(e) =>
                                      updateOptionForm(attribute.id, {
                                        hexColor: e.target.value,
                                      })
                                    }
                                    disabled={savingOptionFor === attribute.id}
                                  />
                                  <input
                                    type="text"
                                    value={optionForm.hexColor}
                                    onChange={(e) =>
                                      updateOptionForm(attribute.id, {
                                        hexColor: e.target.value,
                                      })
                                    }
                                    disabled={savingOptionFor === attribute.id}
                                  />
                                </div>
                              </div>
                            )}
                            {supportsOptionImage && (
                              <div className="form-row">
                                <label
                                  htmlFor={`option-image-${attribute.id}`}
                                >
                                  Imagen de muestra
                                  {attribute.attribute_type === 'text'
                                    ? ' (opcional)'
                                    : ''}
                                </label>
                                <div className="admin-file-input">
                                  <div className="admin-file-picker">
                                    <label
                                      className={`admin-file-btn admin-file-btn-small ${
                                        uploadingOptionFor === attribute.id ||
                                        savingOptionFor === attribute.id
                                          ? 'disabled'
                                          : ''
                                      }`}
                                      htmlFor={`option-image-${attribute.id}`}
                                    >
                                      {uploadingOptionFor === attribute.id
                                        ? 'Subiendo...'
                                        : 'Seleccionar imagen'}
                                    </label>
                                    <input
                                      id={`option-image-${attribute.id}`}
                                      type="file"
                                      accept="image/*"
                                      onChange={(e) =>
                                        handleOptionImageUpload(
                                          attribute.id,
                                          e
                                        )
                                      }
                                      disabled={
                                        uploadingOptionFor === attribute.id ||
                                        savingOptionFor === attribute.id
                                      }
                                    />
                                  </div>
                                  <span className="admin-file-text">
                                    {optionForm.imagePath
                                      ? optionForm.imagePath
                                      : 'Ninguna imagen'}
                                  </span>
                                  {optionForm.imagePath && (
                                    <>
                                      <img
                                        src={resolveMediaUrl(
                                          optionForm.imagePath
                                        )}
                                        alt=""
                                        className="admin-attribute-finish-thumb"
                                      />
                                      <button
                                        type="button"
                                        className="admin-file-remove-btn"
                                        onClick={() =>
                                          updateOptionForm(attribute.id, {
                                            imagePath: '',
                                          })
                                        }
                                      >
                                        Quitar
                                      </button>
                                    </>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                          <div className="admin-attribute-card-actions">
                            <button
                              type="submit"
                              className="admin-auth-btn secondary"
                              disabled={
                                savingOptionFor === attribute.id ||
                                !optionForm.label.trim() ||
                                (isImageType && !optionForm.imagePath)
                              }
                            >
                              {savingOptionFor === attribute.id
                                ? 'Agregando...'
                                : 'Agregar valor'}
                            </button>
                            <button
                              type="button"
                              className="admin-table-btn danger"
                              onClick={() => handleDeleteAttribute(attribute)}
                            >
                              Eliminar atributo
                            </button>
                          </div>
                        </form>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {statusMessage && (
            <p className={`admin-auth-status ${statusType}`}>{statusMessage}</p>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminAttributeCatalog;
